import { useEffect } from 'react';
// Import Store
import { useAppDispatch, useAppSelector, useAppStore } from 'store';
import {
    getAdminFullPartnersListCanceled,
    getAdminFullPartnersListFailed,
    getAdminFullPartnersListFetch,
    getAdminFullPartnersListSucceeded,
    selectAdminFullPartnersList,
    selectAdminFullPartnersListStatus,
} from 'store/slices/adminFullPartnersList';
// Import Hooks
import useRequest from './UseRequest';
import useHasPermission from './UseHasPermission';
import useCancelableRequests from './UseCancelableRequests';
// Import Services
import { AdministrationHttpService } from 'services/http';
// Import Enums
import { PermissionEnum } from 'enums';

/**
 * Returns the admin full partners list and its loading state, and requests the list on first use
 * (only for CRMAdmin/CRMManager users). Show the loading state instead of an empty list while `isLoading` is true.
 * The loaded list is shared by all screens; it is requested again after a partner change.
 */
const useAdminFullPartnersList = () => {
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const { doGetRequest } = useRequest();
    const { hasPermission } = useHasPermission();

    const adminFullPartnersList = useAppSelector(selectAdminFullPartnersList);
    const status = useAppSelector(selectAdminFullPartnersListStatus);

    const { getPartnersFullListRequest } = useCancelableRequests({
        getPartnersFullListRequest: AdministrationHttpService.getPartnersFullList,
    });

    const isAllowed = hasPermission([PermissionEnum.CRMAdmin, PermissionEnum.CRMManager]);
    const isIdle = status === 'idle';
    // idle counts as loading too: the request starts right after the first render
    const isLoading = isAllowed && (isIdle || status === 'loading');

    // Runs on mount and when the list is reset to idle after a partner change (`isIdle` is the trigger).
    // A failed request is retried only on the next mount, not in a loop.
    useEffect(() => {
        // The current status, not the render-time one: another screen mounted in the same render
        // may already have started the request.
        const { status: currentStatus } = store.getState().adminFullPartnersList;
        if (!isAllowed || (currentStatus !== 'idle' && currentStatus !== 'failed')) return;

        dispatch(getAdminFullPartnersListFetch());
        doGetRequest(getPartnersFullListRequest.request, {
            successCallback: (data) => {
                dispatch(getAdminFullPartnersListSucceeded(data));
            },
            errorCallback: () => {
                dispatch(getAdminFullPartnersListFailed());
            },
        }).then(({ IsCanceled }) => {
            // A canceled call skips both callbacks, so the status is set back to idle for the next screen
            if (IsCanceled) {
                dispatch(getAdminFullPartnersListCanceled());
            }
        });
    }, [isAllowed, isIdle, store, dispatch, doGetRequest, getPartnersFullListRequest]);

    return { adminFullPartnersList, isLoading };
};

export default useAdminFullPartnersList;
